import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { supabaseBikeService } from "@/api/supabaseBikeService";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { format, differenceInMinutes, addDays, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, isWithinInterval, subDays } from "date-fns";
import { Bike, Clock, Route, CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { exportBookingsAsCSV } from "./exportBookingsUtils";
import { Link } from "react-router-dom";
import { Constants } from "@/integrations/supabase/types";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import DashboardFiltersComponent, { DashboardFilters } from "../dashboard/components/DashboardFilters";

const PAGE_SIZE = 25;

const columns = [
  { id: "date", label: "Date" },
  { id: "user", label: "User" },
  { id: "bike", label: "Bike" },
  { id: "rideType", label: "Ride Type" },
  { id: "status", label: "Status" },
  { id: "startLocation", label: "Start Location" },
  { id: "endLocation", label: "End Location" },
  { id: "duration", label: "Duration" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "unconfirmed", label: "Unconfirmed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "completed", label: "Completed" },
  { value: "unavailable", label: "Unavailable" },
];

const TIME_PERIOD_OPTIONS = [
  { value: "all", label: "All Time" },
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "year", label: "Year" },
];

const BookingsPage = () => {
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [timePeriod, setTimePeriod] = useState("all");
  const [locationFilters, setLocationFilters] = useState<DashboardFilters>({
    regionId: "all",
    organizationId: "all",
    siteId: "all"
  });
  const [now] = useState(() => new Date());

  // Helper function to determine site IDs for filtering
  const getSiteIdsForFilters = async (filters: DashboardFilters): Promise<string[] | null> => {
    if (filters.siteId && filters.siteId !== 'all') {
      return [filters.siteId];
    } else if (filters.organizationId && filters.organizationId !== 'all') {
      const { data: orgSites } = await supabase
        .from('sites')
        .select('id')
        .eq('organization_id', filters.organizationId);
      
      return orgSites && orgSites.length > 0 ? orgSites.map(site => site.id) : null;
    } else if (filters.regionId && filters.regionId !== 'all') {
      const { data: regionOrgs } = await supabase
        .from('organizations')
        .select('id')
        .eq('region_id', filters.regionId);
      
      if (regionOrgs && regionOrgs.length > 0) {
        const orgIds = regionOrgs.map(org => org.id);
        const { data: regionSites } = await supabase
          .from('sites')
          .select('id')
          .in('organization_id', orgIds);
        
        return regionSites && regionSites.length > 0 ? regionSites.map(site => site.id) : null;
      }
    }
    return null;
  };

  const { data, isLoading } = useQuery({
    queryKey: ["all-bookings", page, locationFilters],
    queryFn: async () => {
      console.log("Fetching bookings with location filters", locationFilters);
      
      const siteIdsToFilter = await getSiteIdsForFilters(locationFilters);
      
      let query = supabase
        .from('bookings')
        .select(`
          *,
          user:users(username),
          bike:bikes(serial_number),
          start_site:sites!bookings_start_location_id_fkey(name),
          end_site:sites!bookings_end_location_id_fkey(name)
        `)
        .order('start_time', { ascending: false });
      
      // Apply location filtering if needed
      if (siteIdsToFilter && siteIdsToFilter.length > 0) {
        // Filter by bookings that either start or end at the filtered sites
        query = query.or(`start_location_id.in.(${siteIdsToFilter.join(',')}),end_location_id.in.(${siteIdsToFilter.join(',')})`);
      }
      
      const { data: bookings, error } = await query;
      
      if (error) {
        console.error("Error fetching bookings:", error);
        throw error;
      }
      
      return {
        bookings: bookings || [],
        total: bookings?.length || 0,
      };
    },
    refetchOnWindowFocus: false,
  });

  const filteredBookings = (data?.bookings || []).filter((booking) => {
    let statusMatch = statusFilter === "all" || booking.status === statusFilter;
    let timeMatch = true;
    if (timePeriod !== "all") {
      const start = new Date(booking.start_time);
      let range = null;
      switch (timePeriod) {
        case "day":
          range = { start: startOfDay(now), end: endOfDay(now) };
          break;
        case "week":
          range = { start: startOfDay(subDays(now, 6)), end: endOfDay(now) };
          break;
        case "month":
          range = { start: startOfMonth(now), end: endOfMonth(now) };
          break;
        case "year":
          range = { start: startOfYear(now), end: endOfYear(now) };
          break;
        default:
          break;
      }
      if (range) {
        timeMatch = isWithinInterval(start, range);
      }
    }
    return statusMatch && timeMatch;
  });

  const total = filteredBookings.length;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const bookings = filteredBookings.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const renderCell = (booking, attribute) => {
    switch (attribute) {
      case "date":
        return (
          <div>
            <div className="font-medium">{format(new Date(booking.start_time), "dd MMM yyyy")}</div>
            <div className="text-xs text-muted-foreground">
              {format(new Date(booking.start_time), "HH:mm")} - {format(new Date(booking.end_time), "HH:mm")}
            </div>
          </div>
        );
      case "user": {
        const userId = booking.user_id;
        const username = booking.user?.username || userId;
        return userId ? (
          <Link to={`/users/${userId}`} className="font-medium text-primary hover:underline">
            {username}
          </Link>
        ) : (
          <span className="font-medium">{username}</span>
        );
      }
      case "bike": {
        const bikeId = booking.bike_id || booking.bike;
        const bikeLabel = booking.bike?.serial_number || booking.bike || bikeId || "Unknown";
        return bikeId ? (
          <div className="flex items-center gap-2">
            <Bike className="h-4 w-4 text-muted-foreground" />
            <Link to={`/bikes/${bikeId}`} className="text-primary hover:underline">
              {bikeLabel}
            </Link>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Bike className="h-4 w-4 text-muted-foreground" />
            <span>{bikeLabel}</span>
          </div>
        );
      }
      case "rideType":
        return <span className="capitalize">{booking.ride_type || "-"}</span>;
      case "status":
        return <span className="capitalize">{booking.status || "-"}</span>;
      case "startLocation":
        return (
          <span>
            {booking.start_location_type === "site"
              ? booking.start_site?.name || booking.start_location_id || "Site"
              : "Home"}
          </span>
        );
      case "endLocation":
        return (
          <span>
            {booking.end_location_type === "site"
              ? booking.end_site?.name || booking.end_location_id || "Site"
              : "Home"}
          </span>
        );
      case "duration":
        const start = new Date(booking.start_time);
        const end = new Date(booking.end_time);
        const duration = differenceInMinutes(end, start);
        return (
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span>{duration} min</span>
          </div>
        );
      default:
        return null;
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      // Export only the currently filtered bookings (not paginated)
      exportBookingsAsCSV(filteredBookings);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Bookings</h1>
      <div className="flex justify-end mb-2">
        <Button onClick={handleExportCSV} disabled={exporting || isLoading} variant="outline" size="sm">
          {exporting ? "Exporting..." : "Download CSV"}
        </Button>
      </div>
      
      {/* Location Filters */}
      <div className="mb-4">
        <DashboardFiltersComponent 
          filters={locationFilters}
          onFiltersChange={(filters) => {
            setLocationFilters(filters);
            setPage(1); // Reset to first page when filters change
          }}
        />
      </div>
      
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-end mb-4">
        <div className="flex items-center gap-2">
          <label htmlFor="status" className="font-medium min-w-max text-muted-foreground text-sm">Status:</label>
          <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="period" className="font-medium min-w-max text-muted-foreground text-sm">Period:</label>
          <Select value={timePeriod} onValueChange={v => { setTimePeriod(v); setPage(1); }}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="All Time" />
            </SelectTrigger>
            <SelectContent>
              {TIME_PERIOD_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="bg-white rounded shadow p-4">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead key={column.id}>{column.label}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="text-center">
                  <div className="flex items-center justify-center py-4">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
                    <span className="ml-2">Loading bookings...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : !bookings || bookings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="text-center py-8">
                  <div className="flex flex-col items-center justify-center">
                    <CalendarDays className="h-12 w-12 text-muted-foreground mb-2" />
                    <p className="text-muted-foreground font-medium">No bookings found</p>
                    <p className="text-xs text-muted-foreground">There are no bookings to display at this time.</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              bookings.map((booking) => (
                <TableRow key={booking.id} className="hover:bg-muted/50">
                  {columns.map((column) => (
                    <TableCell key={`${booking.id}-${column.id}`}>{renderCell(booking, column.id)}</TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <div className="flex items-center justify-center mt-4 gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            aria-label="Previous"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          {/* Page numbers logic */}
          {(() => {
            const pages = [];
            const maxPagesToShow = 5;
            let start = Math.max(1, page - 2);
            let end = Math.min(totalPages, page + 2);
            if (page <= 3) {
              end = Math.min(totalPages, maxPagesToShow);
            } else if (page >= totalPages - 2) {
              start = Math.max(1, totalPages - maxPagesToShow + 1);
            }
            if (start > 1) {
              pages.push(
                <button
                  key={1}
                  className={`px-2 py-1 rounded ${page === 1 ? 'bg-muted font-bold' : 'hover:bg-muted'}`}
                  onClick={() => setPage(1)}
                >1</button>
              );
              if (start > 2) pages.push(<span key="start-ellipsis" className="px-1">...</span>);
            }
            for (let i = start; i <= end; i++) {
              pages.push(
                <button
                  key={i}
                  className={`px-2 py-1 rounded ${page === i ? 'bg-muted font-bold' : 'hover:bg-muted'}`}
                  onClick={() => setPage(i)}
                  disabled={page === i}
                >{i}</button>
              );
            }
            if (end < totalPages) {
              if (end < totalPages - 1) pages.push(<span key="end-ellipsis" className="px-1">...</span>);
              pages.push(
                <button
                  key={totalPages}
                  className={`px-2 py-1 rounded ${page === totalPages ? 'bg-muted font-bold' : 'hover:bg-muted'}`}
                  onClick={() => setPage(totalPages)}
                >{totalPages}</button>
              );
            }
            return pages;
          })()}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages || totalPages === 0}
            aria-label="Next"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BookingsPage; 